import { useState, useEffect } from 'react';
import {
  getDebugRuns, getDebugRun,
  PipelineRunSummary, PipelineRun, LocationTrace, SourceTrace, ExcerptTrace,
  ExcerptOutcome,
} from '../api/adminApi';
import styles from './PipelineDebugPage.module.css';

const HIJRI_MONTHS = ['', 'Muharram', 'Safar', "Rabi' al-Awwal", "Rabi' al-Thani",
  'Jumada al-Awwal', 'Jumada al-Thani', 'Rajab', "Sha'ban",
  'Ramadan', 'Shawwal', "Dhu al-Qi'dah", 'Dhu al-Hijjah'];

function fmtMs(ms: number): string {
  if (ms < 1000) return `${ms}ms`;
  return `${(ms / 1000).toFixed(1)}s`;
}

function fmtTime(iso: string): string {
  return new Date(iso).toLocaleString('en-GB', { dateStyle: 'short', timeStyle: 'medium' });
}

function fmtRelative(iso: string): string {
  const diff = Date.now() - new Date(iso).getTime();
  const m = Math.floor(diff / 60000);
  if (m < 1) return 'just now';
  if (m < 60) return `${m}m ago`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h}h ago`;
  return `${Math.floor(h / 24)}d ago`;
}

const OUTCOME_META: Record<ExcerptOutcome, { label: string; color: string }> = {
  detection: { label: 'Detection', color: '#16a34a' },
  not_announcement: { label: 'Not announcement', color: '#64748b' },
  no_api_key: { label: 'No API key', color: '#f59e0b' },
  duplicate: { label: 'Duplicate', color: '#8b5cf6' },
  error: { label: 'Error', color: '#ef4444' },
  stale: { label: 'Stale', color: '#94a3b8' },
};

// ─── Excerpt row ──────────────────────────────────────────────────────────────

function ExcerptRow({ ex }: { ex: ExcerptTrace }) {
  const [open, setOpen] = useState(false);
  const meta = OUTCOME_META[ex.outcome];
  return (
    <div className={styles.excerptRow}>
      <button className={styles.excerptToggle} onClick={() => setOpen(v => !v)}>
        <span className={styles.excerptCaret}>{open ? '▾' : '▸'}</span>
        <span className={styles.excerptPreview}>{ex.text.slice(0, 80)}{ex.text.length > 80 ? '…' : ''}</span>
        <span className={styles.excerptTiming}>{fmtMs(ex.analyzeMs)}</span>
        <span className={styles.outcomePill} style={{ background: meta.color + '1a', color: meta.color }}>{meta.label}</span>
      </button>
      {open && (
        <div className={styles.excerptDetail}>
          <div className={styles.detailBlock}>
            <span className={styles.detailLabel}>Full text</span>
            <p className={styles.detailText}>{ex.text}</p>
          </div>
          {ex.sourceUrl && (
            <div className={styles.detailBlock}>
              <span className={styles.detailLabel}>Source URL</span>
              <a className={styles.detailLink} href={ex.sourceUrl} target="_blank" rel="noreferrer">{ex.sourceUrl}</a>
            </div>
          )}
          {ex.extracted && (
            <div className={styles.detailBlock}>
              <span className={styles.detailLabel}>Claude output</span>
              <div className={styles.extractedGrid}>
                {ex.extracted.hijriMonth != null && (
                  <span><strong>Month:</strong> {HIJRI_MONTHS[ex.extracted.hijriMonth] ?? ex.extracted.hijriMonth} ({ex.extracted.hijriYear ?? '?'})</span>
                )}
                {ex.extracted.startDate && <span><strong>Start date:</strong> {ex.extracted.startDate}</span>}
                <span><strong>Confidence:</strong> {ex.extracted.confidence}</span>
                <span className={styles.reasoning}><strong>Reasoning:</strong> {ex.extracted.reasoning}</span>
              </div>
            </div>
          )}
          {ex.error && (
            <div className={styles.detailBlock}>
              <span className={styles.detailLabel}>Error</span>
              <p className={styles.detailError}>{ex.error}</p>
            </div>
          )}
          {ex.detectionId && (
            <div className={styles.detailBlock}>
              <span className={styles.detailLabel}>Saved as detection</span>
              <code className={styles.detailCode}>{ex.detectionId}</code>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// ─── Source block ─────────────────────────────────────────────────────────────

function SourceBlock({ src }: { src: SourceTrace }) {
  const [open, setOpen] = useState(src.status === 'error' || src.excerptCount > 0);
  const detections = src.excerpts.filter(e => e.outcome === 'detection').length;
  const errors = src.excerpts.filter(e => e.outcome === 'error').length + (src.status === 'error' ? 1 : 0);
  const noKey = src.excerpts.some(e => e.outcome === 'no_api_key');

  return (
    <div className={`${styles.sourceBlock} ${src.status === 'error' && src.excerptCount === 0 ? styles.sourceError : ''}`}>
      <button className={styles.sourceHeader} onClick={() => setOpen(v => !v)}>
        <span className={styles.sourceCaret}>{open ? '▾' : '▸'}</span>
        <span className={styles.sourceName}>{src.name}</span>
        <span className={`${styles.sourceTypePill} ${styles[`type_${src.type}`]}`}>{src.type}</span>
        <div className={styles.sourceMeta}>
          <span className={styles.sourceCount}>{src.excerptCount} excerpt{src.excerptCount !== 1 ? 's' : ''}</span>
          {src.fetchMs > 0 && <span className={styles.sourceTiming}>{fmtMs(src.fetchMs)}</span>}
          {detections > 0 && <span className={styles.sourceDetectionBadge}>{detections} detection{detections > 1 ? 's' : ''}</span>}
          {noKey && <span className={styles.sourceWarnBadge}>no API key</span>}
          {errors > 0 && <span className={styles.sourceErrorBadge}>{errors} error{errors > 1 ? 's' : ''}</span>}
        </div>
      </button>
      {open && (
        <div className={styles.sourceBody}>
          {src.error && (
            <div className={styles.sourceFetchError}>
              <span className={styles.errorIcon}>✕</span> {src.error}
            </div>
          )}
          {src.excerpts.length === 0 && !src.error && (
            <p className={styles.noExcerpts}>No excerpts matched keywords.</p>
          )}
          {src.excerpts.map((ex, i) => <ExcerptRow key={i} ex={ex} />)}
        </div>
      )}
    </div>
  );
}

// ─── Location block ───────────────────────────────────────────────────────────

function LocationBlock({ loc }: { loc: LocationTrace }) {
  const [open, setOpen] = useState(loc.decision === 'scrape');
  const totalDetections = loc.sources.flatMap(s => s.excerpts).filter(e => e.outcome === 'detection').length;
  const totalErrors = loc.sources.filter(s => s.status === 'error' && s.excerpts.length === 0).length +
    loc.sources.flatMap(s => s.excerpts).filter(e => e.outcome === 'error').length;

  return (
    <div className={`${styles.locationBlock} ${loc.decision === 'skip' ? styles.locationSkipped : ''}`}>
      <button className={styles.locationHeader} onClick={() => setOpen(v => !v)}>
        <span className={styles.locationCaret}>{open ? '▾' : '▸'}</span>
        <span className={styles.locationCode}>{loc.locationCode}</span>
        <span className={`${styles.decisionPill} ${loc.decision === 'scrape' ? styles.decisionScrape : styles.decisionSkip}`}>
          {loc.decision}
        </span>
        <span className={styles.locationReason}>{loc.decisionReason}</span>
        <div className={styles.locationMeta}>
          {loc.hijriDay !== null && <span className={styles.dayBadge}>Day {loc.hijriDay}</span>}
          <span className={styles.locationTiming}>{fmtMs(loc.dayCheckMs)}</span>
          {totalDetections > 0 && <span className={styles.locationDetBadge}>{totalDetections} new detection{totalDetections > 1 ? 's' : ''}</span>}
          {totalErrors > 0 && <span className={styles.locationErrBadge}>{totalErrors} error{totalErrors > 1 ? 's' : ''}</span>}
        </div>
      </button>
      {open && loc.sources.length > 0 && (
        <div className={styles.locationBody}>
          {loc.sources.map((src, i) => <SourceBlock key={i} src={src} />)}
        </div>
      )}
      {open && loc.decision === 'skip' && (
        <p className={styles.skippedNote}>Skipped — not in sighting window. Use "Force run" to override.</p>
      )}
    </div>
  );
}

// ─── Run row (summary list) ───────────────────────────────────────────────────

function RunRow({ run, selected, onClick }: { run: PipelineRunSummary; selected: boolean; onClick: () => void }) {
  return (
    <button className={`${styles.runRow} ${selected ? styles.runRowSelected : ''}`} onClick={onClick}>
      <div className={styles.runRowTop}>
        <span className={styles.runTime}>{fmtTime(run.startedAt)}</span>
        {run.forced && <span className={styles.forcedTag}>forced</span>}
        <span className={styles.runAge}>{fmtRelative(run.startedAt)}</span>
      </div>
      <div className={styles.runRowStats}>
        <span>{run.locationsScraped}/{run.locationsScraped + run.locationsSkipped} scraped</span>
        <span>{run.totalExcerpts} excerpts</span>
        {run.totalDetections > 0 && <span className={styles.statDetection}>{run.totalDetections} detection{run.totalDetections > 1 ? 's' : ''}</span>}
        {run.totalErrors > 0 && <span className={styles.statError}>{run.totalErrors} error{run.totalErrors > 1 ? 's' : ''}</span>}
        <span className={styles.statDuration}>{fmtMs(run.durationMs)}</span>
      </div>
    </button>
  );
}

// ─── Run detail ───────────────────────────────────────────────────────────────

function RunDetail({ run }: { run: PipelineRun }) {
  return (
    <div className={styles.runDetail}>
      <div className={styles.runDetailHeader}>
        <div className={styles.runDetailMeta}>
          <span className={styles.runDetailTime}>{fmtTime(run.startedAt)}</span>
          {run.forced && <span className={styles.forcedTag}>forced</span>}
        </div>
        <div className={styles.runDetailStats}>
          <Stat label="Duration" value={fmtMs(run.durationMs)} />
          <Stat label="Scraped" value={`${run.locationsScraped} loc`} />
          <Stat label="Excerpts" value={String(run.totalExcerpts)} />
          <Stat label="Analyzed" value={String(run.totalAnalyzed)} />
          <Stat label="Detections" value={String(run.totalDetections)} color={run.totalDetections > 0 ? '#16a34a' : undefined} />
          <Stat label="Errors" value={String(run.totalErrors)} color={run.totalErrors > 0 ? '#ef4444' : undefined} />
        </div>
      </div>
      <div className={styles.locationList}>
        {run.locations.map((loc, i) => <LocationBlock key={i} loc={loc} />)}
      </div>
    </div>
  );
}

function Stat({ label, value, color }: { label: string; value: string; color?: string }) {
  return (
    <div className={styles.statBox}>
      <span className={styles.statValue} style={{ color }}>{value}</span>
      <span className={styles.statLabel}>{label}</span>
    </div>
  );
}

// ─── Page ─────────────────────────────────────────────────────────────────────

interface Props { highlightRunId?: string; }

export default function PipelineDebugPage({ highlightRunId }: Props) {
  const [runs, setRuns] = useState<PipelineRunSummary[]>([]);
  const [selectedId, setSelectedId] = useState<string | null>(highlightRunId ?? null);
  const [detail, setDetail] = useState<PipelineRun | null>(null);
  const [loadingRuns, setLoadingRuns] = useState(true);
  const [loadingDetail, setLoadingDetail] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    setLoadingRuns(true);
    getDebugRuns()
      .then(r => {
        setRuns(r);
        // Only auto-select first run if no highlight was requested
        if (r.length > 0 && !highlightRunId) setSelectedId(r[0].id);
        // If a highlight was requested, select it (it may not be first)
        if (highlightRunId && r.some(run => run.id === highlightRunId)) setSelectedId(highlightRunId);
      })
      .catch(() => setError('Failed to load runs'))
      .finally(() => setLoadingRuns(false));
  }, [highlightRunId]);

  useEffect(() => {
    if (!selectedId) return;
    setLoadingDetail(true);
    setDetail(null);
    getDebugRun(selectedId)
      .then(setDetail)
      .catch(() => setError('Failed to load run detail'))
      .finally(() => setLoadingDetail(false));
  }, [selectedId]);

  if (loadingRuns) {
    return <div className={styles.centered}><div className={styles.spinner} /></div>;
  }

  if (runs.length === 0) {
    return (
      <div className={styles.centered}>
        <p className={styles.emptyMsg}>No pipeline runs recorded yet.</p>
        <p className={styles.emptyHint}>Trigger a run from the Inbox page to see the full trace here.</p>
      </div>
    );
  }

  return (
    <div className={styles.layout}>
      {/* Left: run list */}
      <aside className={styles.sidebar}>
        <div className={styles.sidebarHeader}>
          <span className={styles.sidebarTitle}>Runs</span>
          <span className={styles.sidebarCount}>{runs.length}</span>
        </div>
        <div className={styles.runList}>
          {runs.map(r => (
            <RunRow
              key={r.id}
              run={r}
              selected={r.id === selectedId}
              onClick={() => setSelectedId(r.id)}
            />
          ))}
        </div>
      </aside>

      {/* Right: detail */}
      <main className={styles.detailPane}>
        {error && <div className={styles.errorBanner}>{error}</div>}
        {loadingDetail && <div className={styles.centered}><div className={styles.spinner} /></div>}
        {!loadingDetail && detail && <RunDetail run={detail} />}
      </main>
    </div>
  );
}
